import PropTypes from "prop-types"
import React, { useState } from "react"

import Button from "src/components/Button"

const alertBannerStyles = {
  info: {
    banner: "border-blue-900",
    icon: "fa-circle-exclamation text-blue-900",
    iconStyle: "solid",
    backgroundColor: "bg-blue-100",
  },
  warning: {
    banner: "border-yellow-900",
    icon: "fa-circle-exclamation text-yellow-900",
    iconStyle: "solid",
    backgroundColor: "bg-yellow-100",
  },
  error: {
    banner: "border-red-900",
    icon: "fa-circle-exclamation text-red-900",
    iconStyle: "solid",
    backgroundColor: "bg-red-100",
  },
  success: {
    banner: "border-teal-900",
    icon: "fa-circle-check text-teal-900",
    iconStyle: "solid",
    backgroundColor: "bg-teal-100",
  },
  neutral: {
    banner: "border-gray-600",
    icon: null,
    backgroundColor: "bg-gray-100",
  },
  announcement: {
    banner: "border-teal-900",
    icon: "fa-megaphone text-teal-900",
    iconStyle: "solid",
    backgroundColor: "bg-teal-100",
  },
  recommendation: {
    banner: "border-supplementary-violet-500",
    icon: "fa-stars text-supplementary-violet-500",
    iconStyle: "solid",
    backgroundColor: "bg-supplementary-violet-200",
  },
}

const AlertBanner = ({
  children,
  closeable = false,
  cta,
  title,
  type = "warning",
  onClose,
  flush = false,
}) => {
  const {
    banner,
    icon,
    iconStyle = "kit",
    backgroundColor,
  } = alertBannerStyles[type]
  const [visible, setVisible] = useState(true)

  const renderCta = () => {
    return (
      <Button
        variant={cta.type}
        onClick={cta.onClick}
        isLoading={cta.isLoading}
      >
        {cta.text}
      </Button>
    )
  }

  const handleClose = () => {
    setVisible(false)
    if (onClose) {
      onClose()
    }
  }

  if (visible) {
    return (
      <div
        data-design-system="AlertBanner"
        role="alert"
        className={`${
          flush
            ? ""
            : `rounded-sm border-b border-l-4 border-r border-t shadow-md ${banner}`
        } p-4 ${backgroundColor} flex justify-between gap-4`}
      >
        <div className="flex items-center justify-start gap-4">
          {icon && (
            <i
              data-testid="alert-banner-icon"
              className={`fa-${iconStyle} ${icon} flex items-center justify-center`}
            />
          )}
          <div>
            {title && (
              <h4 className="mb-1 mt-0 text-base font-semibold text-gray-900">
                {title}
              </h4>
            )}
            {children && <div className="text-gray-900">{children}</div>}
          </div>
        </div>
        <div className="flex items-center justify-end gap-4">
          {cta && renderCta()}
          {closeable && (
            <button
              className="fa-solid fa-xmark bg-transparent px-0 py-1 text-xxs text-gray-600"
              onClick={handleClose}
              data-testid="alert-banner-close-x-btn"
            />
          )}
        </div>
      </div>
    )
  }
}

export const alertBannerProps = {
  children: PropTypes.node,
  closeable: PropTypes.bool,
  cta: PropTypes.shape({
    type: PropTypes.oneOf(["primary", "secondary"]).isRequired,
    text: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
    isLoading: PropTypes.bool,
  }),
  title: PropTypes.string,
  type: PropTypes.oneOf([
    "info",
    "warning",
    "error",
    "success",
    "neutral",
    "announcement",
    "recommendation",
  ]),
  onClose: PropTypes.func,
  flush: PropTypes.bool,
}

AlertBanner.propTypes = alertBannerProps

export default AlertBanner
